import React from "react";
import {
  CloseDrawerButtonWithIcon,
  SubmitDrawerButtonWithIcon,
  TopButtonsGroup,
} from "@src/components/ContentDrawer/DrawerComponents";
import styled from "styled-components";
import { theme } from "@src/theme";
import TagSection from "./components/TagSection";
import { useFetchTagsInfiniteQuery } from "@src/queries/tagQueries";
import { useForm } from "react-hook-form";
import { useDebounce } from "@src/hooks/useDebounce";
import { AxiosResponse } from "axios";
import { ApiResponse } from "@src/types/httpApi";
import { ITag } from "@src/types/tags";
import { useAppDispatch, useAppSelector } from "@src/redux/hooks";
import {
  setTagFactoryFilters,
  resetTagFactoryFilters,
} from "@src/redux/slices/tagFactorySlice";
import { UIButton } from "@src/components/UIButton";
import Tooltip from "@src/components/Tooltip";
import MultiSelect from "@src/components/MultiSelect";
import { SearchInput } from "@src/components/SearchInput";
import { useMemo, useEffect, useState } from "react";
import DropDownTab from "@src/components/DropDownTab";
import useDropDownTab from "@src/components/DropDownTab/useDropDownTab";

const ItemsContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  width: 100%;
  gap: ${theme.margins.large};
`;

type TagFilterProps = {
  onClose?: () => void;
};

const TagFilter = ({ onClose }: TagFilterProps) => {
  const dispatch = useAppDispatch();
  const { filters } = useAppSelector((state) => state.tagFactory);
  const { register, watch } = useForm({
    defaultValues: { searchText: "" },
  });
  const searchText = useDebounce<string>(watch("searchText"), 300);
  const { data: tagsResponse } = useFetchTagsInfiniteQuery({
    searchText,
  });

  const {
    closeDropdown: closeTagSection,
    isOpen: isTagSectionOpen,
    toggleDropdown: toggleTagSection,
    openDropdown: openTagSection,
  } = useDropDownTab();

  // Initialize selectedTags from Redux state
  const initialSelectedTags = useMemo(() => {
    return filters.tags || [];
  }, [filters.tags]);

  const [selectedTags, setSelectedTags] = useState<ITag[]>(initialSelectedTags);

  // Sync local state with Redux when filters are reset
  useEffect(() => {
    setSelectedTags(filters.tags || []);
  }, [filters.tags]);

  // Update Redux when selectedTags change
  useEffect(() => {
    dispatch(setTagFactoryFilters({ tags: selectedTags }));
  }, [selectedTags, dispatch]);

  const tags = useMemo(() => {
    return (
      (
        (tagsResponse?.pages as AxiosResponse<ApiResponse<ITag[]>>[]) ?? []
      ).flatMap((response) => response.data.data) || []
    );
  }, [tagsResponse]);

  const handleClose = () => {
    // Just close the drawer, don't reset filters
    if (onClose) {
      onClose();
    }
  };

  const handleResetFilters = () => {
    // Reset all filters
    dispatch(resetTagFactoryFilters());
    setSelectedTags([]);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleClose();
  };

  // Check if there are any active filters
  const hasActiveFilters =
    (filters.genre && filters.genre.length > 0) ||
    (filters.emotion && filters.emotion.length > 0) ||
    (filters.tags && filters.tags.length > 0);

  return (
    <form onSubmit={handleSubmit}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: "100%",
        }}
      >
        <TopButtonsGroup>
          <CloseDrawerButtonWithIcon onClick={handleClose} disabled={false} />
          <div style={{ display: "flex", gap: theme.margins.medium }}>
            <Tooltip
              content={
                hasActiveFilters ? "Clear filters" : "No filters to reset"
              }
              position="top"
            >
              <UIButton
                style={{ minWidth: "100px" }}
                type="button"
                onClick={handleResetFilters}
                variant={
                  hasActiveFilters ? "primaryOutline" : "secondaryOutline"
                }
                disabled={!hasActiveFilters}
              >
                Reset filters
              </UIButton>
            </Tooltip>
            <SubmitDrawerButtonWithIcon disabled={false} />
          </div>
        </TopButtonsGroup>
        <ItemsContainer>
          <TagSection tagType="genre" />
          <TagSection tagType="feeling" />
          <DropDownTab
            contentContainerStyles={{
              paddingLeft: 0,
              paddingRight: 0,
              paddingTop: theme.margins.medium,
              backgroundColor: theme.colors.gray300,
            }}
            isOpen={isTagSectionOpen}
            toggleDropdown={toggleTagSection}
            closeDropdown={closeTagSection}
            openDropdown={openTagSection}
            title="Tags"
            key={1}
          >
            <div>
              <div
                style={{
                  paddingBottom: theme.margins.medium,
                }}
              >
                <SearchInput
                  placeholder="Search by name"
                  {...register("searchText")}
                />
              </div>
              <MultiSelect<ITag>
                optionEntityName="tag"
                options={tags}
                labelKey={"title"}
                valueKey={"documentId"}
                setSelected={setSelectedTags}
                selected={selectedTags}
                enableCreate={false}
                menuItems={[]}
              />
            </div>
          </DropDownTab>
        </ItemsContainer>
      </div>
    </form>
  );
};

export default TagFilter;
